import { useMutation, useQueryClient } from '@tanstack/react-query'

import { taskKeys } from '../queryKeys'
import { CreateTaskData, Step, StepFormData, Task, UpdateTaskData } from '../types/tasks'
import { useTasksApi } from './tasksApi'

export const useTasksMutation = () => {
  const queryClient = useQueryClient()
  const {
    createTask,
    updateTask,
    deleteTask,
    createStep,
    updateStep,
    deleteStep,
    updateTaskStatus,
  } = useTasksApi()

  // Update this function to be more specific with invalidations
  const invalidateTaskQueries = (task: Task) => {
    // Invalidate all task-related queries
    queryClient.invalidateQueries(taskKeys.lists())

    // Specifically invalidate the group by project query
    queryClient.invalidateQueries(taskKeys.list({ groupByProject: true }))

    // Invalidate the specific task detail if needed
    queryClient.invalidateQueries(taskKeys.detail(task.id))
  }

  // Add new mutation for updating task status
  const updateTaskStatusMutation = useMutation<
    Task,
    Error,
    { taskId: string; status: Task['status'] }
  >(
    async ({ taskId, status }) => {
      console.log('Updating task status:', taskId, 'to:', status)
      const updatedTask = await updateTaskStatus(taskId, status)
      console.log('Task status updated successfully')
      return updatedTask
    },
    {
      onSuccess: (task) => {
        console.log('Task status update successful, invalidating queries')
        invalidateTaskQueries(task)
      },
    }
  )

  // Add this mutation alongside the other mutations
  const updateStepStatusMutation = useMutation<
    void,
    Error,
    { taskId: string; stepId: string; status: 'completed' | 'incomplete' | 'deleted' }
  >(
    async ({ taskId, stepId, status }) => {
      const apiStatus = status === 'completed' ? 'completed' : status === 'deleted' ? 'deleted' : 'incomplete'
      await updateStep(taskId, stepId, { status: apiStatus })

      // COMMENTED OUT: This was causing focus_at and focus_at_date to be reset to null
      // const updatedTask = await updateTask(taskId, {}
      // We don't need to return anything - the mutation will just invalidate queries
      // This avoids having to create a fake Task object
      //const updatedTask = await updateTask(taskId, {})
      //return updatedTask
    },
    {
      onSuccess: (_, variables) => {
        // Invalidate relevant queries to ensure fresh data across the app
        queryClient.invalidateQueries(taskKeys.lists())
        queryClient.invalidateQueries(taskKeys.list({ groupByProject: true }))
        queryClient.invalidateQueries(taskKeys.detail(variables.taskId))
      },
    }
  )

  // Mutation for creating a new task
  const createTaskMutation = useMutation<
    Task,
    Error,
    { taskData: CreateTaskData; steps: StepFormData[] }
  >(
    async ({ taskData, steps }) => {
      console.log('Creating task with data:', { ...taskData })
      const newTask = await createTask(taskData)
      console.log('Task created:', newTask)

      if (steps.length > 0) {
        console.log('Creating steps for new task:', newTask.id)
        await Promise.all(steps.map((step) => createStep(newTask.id, step)))
        console.log('Steps created successfully')
      }

      return newTask
    },
    {
      onSuccess: (task) => {
        console.log('Task creation successful, invalidating queries')
        invalidateTaskQueries(task)
      },
    }
  )

  // Mutation for updating an existing task
  const updateTaskMutation = useMutation<
    Task,
    Error,
    {
      taskId: string
      taskData: UpdateTaskData
      initialStepData: StepFormData[]
      stepData: StepFormData[]
    }
  >(
    async ({ taskId, taskData, initialStepData, stepData }) => {
      console.log('Updating task:', taskId, 'with data:', {
        ...taskData,
        initialStepData,
        stepData,
      })
      const updatedTask = await updateTask(taskId, taskData)

      // Filter out steps with empty names
      const validStepData = stepData.filter((step) => step.name && step.name.trim() !== '')

      // Identify steps to update, create, and delete
      const stepsToUpdate: StepFormData[] = []
      const stepsToCreate: StepFormData[] = []
      const stepsToDelete = initialStepData.filter(
        (initialStep) => !validStepData.some((step) => step.id === initialStep.id)
      )

      validStepData.forEach((step) => {
        const initialStep = initialStepData.find((initStep) => initStep.id === step.id)
        if (initialStep) {
          // Check if the step has changed
          if (
            initialStep.name !== step.name ||
            initialStep.description !== step.description ||
            initialStep.status !== step.status
          ) {
            stepsToUpdate.push(step)
          }
        } else {
          stepsToCreate.push(step)
        }
      })

      // Delete steps that are no longer present
      for (const stepToDelete of stepsToDelete) {
        if (stepToDelete.id) {
          await deleteStep(taskId, stepToDelete.id)
        }
      }

      // Update changed steps
      for (const step of stepsToUpdate) {
        if (step.id) {
          await updateStep(taskId, step.id, step)
        }
      }

      // Create new steps
      for (const step of stepsToCreate) {
        await createStep(taskId, step)
      }

      return updatedTask
    },
    {
      onSuccess: (task) => {
        console.log('Task update successful, invalidating queries')
        invalidateTaskQueries(task)
      },
    }
  )

  // Mutation for deleting a task
  const deleteTaskMutation = useMutation<void, Error, string>(
    async (taskId) => {
      console.log('Deleting task:', taskId)
      await deleteTask(taskId)
      console.log('Task deleted successfully')
    },
    {
      onSuccess: (_, taskId) => {
        console.log('Task deletion successful, invalidating queries')
        queryClient.invalidateQueries(taskKeys.lists())
        queryClient.invalidateQueries(taskKeys.list({ groupByProject: true }))
      },
    }
  )

  // Return mutation functions and status indicators
  return {
    createTask: createTaskMutation.mutateAsync,
    updateTask: updateTaskMutation.mutateAsync,
    updateTaskStatus: updateTaskStatusMutation.mutateAsync,
    updateStepStatus: updateStepStatusMutation.mutateAsync,
    deleteTask: deleteTaskMutation.mutateAsync,
    isLoading:
      createTaskMutation.isLoading ||
      updateTaskMutation.isLoading ||
      deleteTaskMutation.isLoading ||
      updateTaskStatusMutation.isLoading ||
      updateStepStatusMutation.isLoading,
    error:
      createTaskMutation.error ||
      updateTaskMutation.error ||
      deleteTaskMutation.error ||
      updateTaskStatusMutation.error ||
      updateStepStatusMutation.error,
  }
}
